import { Button } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import {
  useDecisionTree,
  setSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useSearch } from '@provider-directory/contexts/ProviderSearchContext';
import type { ConversionContext } from '@provider-directory/lib/analytics/conversions';
import { DecisionTreeMode } from '@provider-directory/models/decision-tree.model';
import type {
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';

interface BookAnotherProviderArrowButtonProps
  extends Pick<ConversionContext, 'linkContextPage' | 'linkContextComponent'> {
  provider: ProviderResource;
  onOpenDecisionTree?: () => void;
  className?: string;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
}

export const BookAnotherProviderArrowButton = ({
  provider,
  onOpenDecisionTree,
  className,
  searchCriteria,
  linkContextPage,
  linkContextComponent,
}: BookAnotherProviderArrowButtonProps): ReactElement => {
  const { t } = useTranslation();
  const { setMode } = useDecisionTree();
  const { clickedBookAnotherProviderEvent } = useAnalytics();

  const { specialtyNames } = useSearch();
  const handleBookAnotherProvider = (): void => {
    clickedBookAnotherProviderEvent({
      provider,
      searchCriteria: {
        ...searchCriteria,
        specialties: specialtyNames,
      },
      linkContextPage,
      linkContextComponent,
    });
    setSchedulingFlowType('BookAnotherProvider');
    setMode(DecisionTreeMode.RedirectOnRfvSelect);
    onOpenDecisionTree?.();
  };

  return (
    <Button
      variant={'clientLink'}
      testId={`provider-${provider.npi}-book-another-provider-arrow-btn`}
      className={clsx(
        'align-center ml-auto flex gap-2 py-[10px] pr-1',
        'text-display-2xsmall font-semibold text-primary-color',
        className,
      )}
      onClick={handleBookAnotherProvider}
    >
      {t('search.bookAnotherProvider')}
      <MaterialSymbol
        data-testid={'book-another-provider-btn-icon'}
        name={'arrow_right_alt'}
        size={20}
      />
    </Button>
  );
};
